import { useState } from "react";
import { toast } from "sonner";
import { X, Check, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { parseFieldErrors } from "../lib/formErrors";
import DatePickerIT from "./ui/date-picker-it";

/**
 * Bug 19/50 · Modal riusabile "Nuova meta" (personal_goal).
 * Estratto da Mete.jsx per essere invocabile da Home / FAB / altre superfici.
 *
 * Props:
 *   - open: bool
 *   - defaultHorizon: "1_year" | "5_years" | "10_years"
 *   - onClose(): void
 *   - onCreated(newGoal): void
 */
const HORIZONS = [
  { v: "1_year",   label: "1 anno" },
  { v: "5_years",  label: "5 anni" },
  { v: "10_years", label: "10 anni" },
];

export default function MetaCreateModal({ open, defaultHorizon = "1_year", onClose, onCreated }) {
  const [form, setForm] = useState({
    horizon: defaultHorizon, title: "", description: "", category: "", target_date: "",
  });
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error("Il titolo deve avere almeno 3 caratteri"); return; }
    setBusy(true);
    try {
      const created = await api.createGoal({
        horizon: form.horizon,
        title: form.title,
        description: form.description || null,
        category: form.category || null,
        target_date: form.target_date || null,
      });
      toast.success("Meta creata · impegno registrato");
      onCreated?.(created);
      onClose?.();
      // reset per prossima apertura
      setForm({ horizon: defaultHorizon, title: "", description: "", category: "", target_date: "" });
    } catch (e) { toast.error(parseFieldErrors(e, "Errore creazione meta")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="meta-create-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold">Nuova meta</h3>
          <button onClick={onClose} data-testid="meta-create-close" aria-label="Chiudi"><X size={20}/></button>
        </div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Orizzonte</div>
          <div className="inline-flex rounded-full border overflow-hidden">
            {HORIZONS.map((h) => (
              <button key={h.v} type="button"
                onClick={() => setForm({ ...form, horizon: h.v })}
                className={`px-3 py-1.5 text-xs ${form.horizon === h.v ? "bg-white shadow-inner font-semibold" : "opacity-70"}`}
                data-testid={`meta-create-horizon-${h.v}`}>
                {h.label}
              </button>
            ))}
          </div>
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Titolo *</div>
          <input className="pxos-input" value={form.title} maxLength={200}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 data-testid="meta-create-title"/>
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Descrizione</div>
          <textarea className="pxos-input" rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="meta-create-description"/>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Categoria</div>
            <input className="pxos-input" value={form.category} maxLength={80}
                   onChange={(e) => setForm({ ...form, category: e.target.value })}
                   placeholder="es. Salute, Business…"
                   data-testid="meta-create-category"/>
          </label>
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Data target</div>
            <DatePickerIT value={form.target_date}
                          onChange={(v) => setForm({ ...form, target_date: v })}
                          testId="meta-create-target-date" placeholder="gg/mm/aaaa"/>
          </label>
        </div>
        <div className="flex items-start gap-2 rounded-md p-2 text-[11px]"
             style={{ background: "#FFF8E1", color: "#8B6D0A" }}>
          <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
          <div>Puoi <b>modificare</b> questa meta anche dopo. Se cambia il vento, non serve buttarla via.</div>
        </div>
        <div className="flex gap-2 pt-2 justify-end">
          <button onClick={onClose} className="text-sm underline opacity-70">Annulla</button>
          <button disabled={busy} onClick={submit}
                  className="pxos-btn-primary text-sm inline-flex items-center gap-1"
                  data-testid="meta-create-submit">
            <Check size={14}/> Sono pronta, crea
          </button>
        </div>
      </div>
    </div>
  );
}
